import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { PageHeader, StatCard, Loading, GradeBadge, fmtDate } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Activity, BookOpen, ListChecks, Sparkles, MessageSquare, School, Search, Trash2, CreditCard, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { GrantDialog, GrantGedDialog, AddFreeAccountDialog } from "@/pages/AdminGrants";
import { Badge } from "@/components/ui/badge";
import { Gift, UserPlus } from "lucide-react";
import { CredentialReviewPanel } from "@/components/Credentials";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [tick, setTick] = useState(0);
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantGedTarget, setGrantGedTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const loadStats = useCallback(() => api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(errMsg(e))), []);
  const loadCourses = useCallback(() => api.get("/courses").then(({ data }) => setCourses(data)), []);
  useEffect(() => { loadStats(); loadCourses(); }, [loadStats, loadCourses]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/users", { params: { q, role: role === "all" ? undefined : role } }).then(({ data }) => setUsers(data)), 250);
    return () => clearTimeout(t);
  }, [q, role, tick]);

  const revoke = async (u) => {
    try {
      await api.delete(`/admin/users/${u.user_id}/comp`);
      toast.success(`Free access removed for ${u.name}`);
      setTick((x) => x + 1);
      loadStats();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const revokeGed = async (u) => {
    try {
      await api.delete(`/ged/admin/users/${u.user_id}/comp`);
      toast.success(`Free GED Prep removed for ${u.name}`);
      setTick((x) => x + 1);
      loadStats();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const planCell = (u) => {
    if (u.role === "admin") return <span className="text-xs text-muted-foreground">Admin</span>;
    const chips = [];
    if (u.comp_access) chips.push(<Badge key="free" className="bg-[#FFF1C7] text-[#7A5A00] hover:bg-[#FFF1C7]" data-testid="admin-user-plan-free">Free{u.comp_access.expires_at ? ` until ${fmtDate(u.comp_access.expires_at)}` : ""}</Badge>);
    else {
      const s = u.subscription;
      if (s && ["trialing", "active"].includes(s.status)) chips.push(<Badge key="sub" className={s.status === "trialing" ? "bg-[#D7F3F1] text-[#0B5B5B] hover:bg-[#D7F3F1]" : "bg-[#DDF7E7] text-[#14663A] hover:bg-[#DDF7E7]"}>{s.status === "trialing" ? "Trial" : "Paying"} · {s.seats} seat{s.seats === 1 ? "" : "s"}</Badge>);
    }
    if (u.ged_comp) chips.push(
      <Badge key="ged" className="bg-[#FFF8DC] text-[#5A4100] hover:bg-[#FFF8DC] border border-[#F2B705]/30" data-testid="admin-user-ged-free">
        GED free{u.ged_comp.comp_meta?.expires_at ? ` until ${fmtDate(u.ged_comp.comp_meta.expires_at)}` : ""}
      </Badge>
    );
    if (chips.length === 0) return <span className="text-xs text-muted-foreground">{u.subscription?.status || "No plan"}</span>;
    return <div className="flex flex-wrap gap-1">{chips}</div>;
  };

  const update = async (u, patch) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.user_id}`, patch);
      setUsers((arr) => arr.map((x) => (x.user_id === u.user_id ? { ...x, ...data } : x)));
      toast.success("User updated");
      loadStats();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const delCourse = async (c) => {
    if (!window.confirm(`Delete "${c.title}" and all its lessons?`)) return;
    try { await api.delete(`/courses/${c.course_id}`); toast.success("Course deleted"); loadCourses(); loadStats(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!stats) return <Loading />;

  return (
    <div>
      <PageHeader eyebrow="School admin" title="School overview" subtitle="Manage people, roles and content across your online school." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
        <StatCard icon={Users} label="Users" value={stats.users} hint={`${stats.by_role.student} students · ${stats.by_role.teacher} teachers · ${stats.by_role.parent} parents`} testid="admin-stat-users" />
        <StatCard icon={Activity} label="Active today" value={stats.active_today} tone="green" />
        <StatCard icon={ListChecks} label="Quizzes taken" value={stats.quizzes_taken} tone="sky" />
        <StatCard icon={MessageSquare} label="Tutor messages" value={stats.tutor_messages} tone="amber" />
        <StatCard icon={BookOpen} label="Courses" value={stats.courses} />
        <StatCard icon={BookOpen} label="Lessons" value={stats.lessons} tone="green" />
        <StatCard icon={Sparkles} label="AI lessons" value={stats.ai_lessons} tone="sky" />
        <StatCard icon={School} label="Classes" value={stats.classes} tone="coral" />
        <StatCard icon={CreditCard} label="Paying plans" value={stats.subscriptions.active} hint={`${stats.subscriptions.trialing} in free trial`} tone="green" testid="admin-stat-subscriptions" />
        <StatCard icon={Users} label="Family seats" value={stats.subscriptions.family_seats} tone="amber" />
        <StatCard icon={School} label="Classroom seats" value={stats.subscriptions.classroom_seats} tone="sky" />
        <StatCard icon={Gift} label="Free accounts" value={stats.subscriptions.comped} hint={`${stats.subscriptions.canceled} canceled plans`} tone="coral" testid="admin-stat-free" />
      </div>

      <Tabs defaultValue="users" className="mt-10">
        <TabsList className="bg-secondary mb-4">
          <TabsTrigger value="users" data-testid="admin-tab-users">People</TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content">Content</TabsTrigger>
          <TabsTrigger value="credentials" data-testid="admin-tab-credentials">Credentials</TabsTrigger>
        </TabsList>
        <TabsContent value="credentials"><CredentialReviewPanel /></TabsContent>
        <TabsContent value="users">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <Button className="h-11 order-last sm:order-none" onClick={() => setAddOpen(true)} data-testid="admin-add-account-button"><UserPlus className="h-4 w-4 mr-2" />Add free account</Button>
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pl-9 h-11 bg-white" data-testid="admin-user-search" />
            </div>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="sm:w-48 h-11 bg-white" data-testid="admin-role-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {["student", "parent", "teacher", "school", "admin"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="bg-white border border-border rounded-[14px] overflow-x-auto">
            <Table data-testid="admin-users-table">
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Grade</TableHead><TableHead>Plan</TableHead><TableHead>Joined</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Free access</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} data-testid="admin-user-row">
                    <TableCell><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div>{u.role === "student" && <Link to={`/records/${u.user_id}`} className="text-xs text-[#0B6B6B] underline" data-testid="admin-user-records-link">Records</Link>}</TableCell>
                    <TableCell>
                      <Select value={u.role || "none"} onValueChange={(v) => v !== "none" && update(u, { role: v })} disabled={u.role === "admin" && u.email.startsWith("admin@")}>
                        <SelectTrigger className="h-9 w-32 capitalize" data-testid="admin-role-change-button"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {!u.role && <SelectItem value="none">Not set</SelectItem>}
                          {["student", "parent", "teacher", "school", "admin"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>{u.grade_band || "—"}</TableCell>
                    <TableCell>{planCell(u)}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                    <TableCell><Switch checked={!u.disabled} onCheckedChange={(v) => update(u, { disabled: !v })} aria-label="Account active" data-testid="admin-user-active-switch" /></TableCell>
                    <TableCell className="text-right">
                      {u.role === "admin" ? null : (
                        <div className="flex gap-1.5 justify-end flex-wrap">
                          {u.ged_comp ? (
                            <Button variant="ghost" size="sm" onClick={() => revokeGed(u)} data-testid="admin-revoke-ged-button" title={`Remove free GED (${u.ged_comp.comp_meta?.note || "no note"})`}>
                              <ScrollText className="h-3.5 w-3.5 mr-1 text-[#7A5A00]" />Remove GED
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" className="bg-white" onClick={() => setGrantGedTarget(u)} data-testid="admin-grant-ged-button">
                              <ScrollText className="h-3.5 w-3.5 mr-1" />Free GED
                            </Button>
                          )}
                          {u.comp_access ? (
                            <Button variant="ghost" size="sm" onClick={() => revoke(u)} data-testid="admin-revoke-free-button">Remove</Button>
                          ) : (
                            <Button variant="outline" size="sm" className="bg-white" onClick={() => setGrantTarget(u)} data-testid="admin-grant-free-button"><Gift className="h-3.5 w-3.5 mr-1" />Grant free</Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="content">
          <div className="bg-white border border-border rounded-[14px] overflow-x-auto">
            <Table data-testid="admin-courses-table">
              <TableHeader><TableRow><TableHead>Course</TableHead><TableHead>Grade</TableHead><TableHead>Subject</TableHead><TableHead>Author</TableHead><TableHead>Lessons</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {courses.map((c) => (
                  <TableRow key={c.course_id}>
                    <TableCell><Link to={`/courses/${c.course_id}`} className="font-medium hover:underline">{c.title}</Link></TableCell>
                    <TableCell><GradeBadge grade={c.grade_band} /></TableCell>
                    <TableCell>{c.subject}</TableCell>
                    <TableCell className="text-muted-foreground">{c.author_name}</TableCell>
                    <TableCell>{c.lesson_count}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => delCourse(c)} aria-label="Delete course" data-testid="admin-course-delete"><Trash2 className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
      <GrantDialog target={grantTarget} onOpenChange={(o) => !o && setGrantTarget(null)} onDone={() => { setGrantTarget(null); setTick((x) => x + 1); loadStats(); }} />
      <GrantGedDialog target={grantGedTarget} onOpenChange={(o) => !o && setGrantGedTarget(null)} onDone={() => { setGrantGedTarget(null); setTick((x) => x + 1); loadStats(); }} />
      <AddFreeAccountDialog open={addOpen} onOpenChange={setAddOpen} onDone={() => { setAddOpen(false); setTick((x) => x + 1); loadStats(); }} />
    </div>
  );
}
